import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, Copy, ExternalLink, Infinity as InfinityIcon, ShieldCheck } from 'lucide-react';
import SectionHeader from './SectionHeader';
import { copyText } from '../utils/copyText';
import './Certificates.css';

// Certificate badge images
import cp from '../images/badges/aws-certified-cloud-practitioner.png';
import da from '../images/badges/aws-certified-developer-associate.png';
import ex from '../images/badges/red-hat-certified-enterprise-application-developer.png';
import hk from '../images/badges/hackerrank.png';
import tf from '../images/badges/tensor_flow.png';
import ms from '../images/badges/microsoft-azure-ai-900.png';

// Dates are ISO strings so the status below can be computed. `expires: null` means no expiry.
const certifications = [
  {
    id: 1,
    title: 'AWS Cloud Practitioner',
    issuer: 'Amazon Web Services',
    issued: '2023-06-26',
    expires: '2026-11-29',
    validationNumber: 'YSQWCTDK81V41Y3N',
    validationLink: 'https://aws.amazon.com/verification',
    credlyLink: 'https://www.credly.com/badges/8ff43340-76b2-4b92-bd5c-652a73af34ba/public_url',
    badgeImage: cp,
    color: '#ff9900'
  },
  {
    id: 2,
    title: 'AWS Developer Associate',
    issuer: 'Amazon Web Services',
    issued: '2023-11-29',
    expires: '2026-11-29',
    validationNumber: 'LGNKEV1BQFF41QWL',
    validationLink: 'https://aws.amazon.com/verification',
    credlyLink: 'https://www.credly.com/badges/4d16a38c-88f2-4ea6-9158-acd442a81fdb/public_url',
    badgeImage: da,
    color: '#ff9900'
  },
  {
    id: 3,
    title: 'Red Hat Certified Enterprise Application Developer (EX-183)',
    issuer: 'Red Hat',
    issued: '2023-08-28',
    expires: '2026-08-28',
    validationNumber: '230-172-413',
    validationLink: 'https://rhtapps.redhat.com/verify?certId=230-172-413',
    credlyLink: 'https://www.credly.com/badges/d6b437ea-f0e6-42db-beef-8af8ca755809/public_url',
    badgeImage: ex,
    color: '#ee0000'
  },
  {
    id: 4,
    title: 'HackerRank - Software Engineer',
    issuer: 'HackerRank',
    issued: '2024-03-17',
    expires: null,
    validationNumber: '3CF7FFA6173D',
    validationLink: 'https://www.hackerrank.com/certificates/3cf7ffa6173d',
    badgeImage: hk,
    color: '#1ba94c'
  },
  {
    id: 5,
    title: 'Google TensorFlow Developer Certificate',
    issuer: 'Google',
    issued: '2024-04-24',
    expires: '2027-04-24',
    validationNumber: '102070419',
    validationLink: 'https://www.credential.net/a1fbec1a-8092-4d32-96ff-bf36f9316c52#acc.fuejC36B',
    badgeImage: tf,
    color: '#ff6f00'
  },
  {
    id: 6,
    title: 'Microsoft - Azure AI Fundamentals',
    issuer: 'Microsoft',
    issued: '2022-11-06',
    expires: null,
    validationNumber: 'E7B4EDF15AD258CC',
    validationLink: 'https://learn.microsoft.com/api/credentials/share/en-us/phanicharan04/E7B4EDF15AD258CC?sharingId=BA019E42FBDD9BA5',
    badgeImage: ms,
    color: '#0078D4'
  }
];

const DAY = 24 * 60 * 60 * 1000;
const ENDING_SOON_DAYS = 90;

const STATUS = {
  active: { label: 'Active', rank: 0 },
  lifetime: { label: 'Lifetime', rank: 0 },
  ending: { label: 'Ending soon', rank: 1 },
  expired: { label: 'Expired', rank: 2 }
};

// Dates are plain calendar days, so they are parsed as local midnight to avoid timezone drift.
const toDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatDate = (iso) =>
  toDate(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// Status and validity-meter values, all derived from the dates and today's date.
const describe = (cert, now) => {
  if (!cert.expires) return { status: 'lifetime', daysLeft: null, elapsed: null };

  const issued = toDate(cert.issued).getTime();
  const expires = toDate(cert.expires).getTime();
  const daysLeft = Math.ceil((expires - now.getTime()) / DAY);
  const elapsed = Math.min(1, Math.max(0, (now.getTime() - issued) / (expires - issued)));

  let status = 'active';
  if (daysLeft < 0) status = 'expired';
  else if (daysLeft <= ENDING_SOON_DAYS) status = 'ending';

  return { status, daysLeft, elapsed };
};

const validityText = (cert, info) => {
  if (info.status === 'lifetime') return 'No expiry';
  if (info.status === 'expired') return `Expired ${formatDate(cert.expires)}`;
  const base = `Valid until ${formatDate(cert.expires)}`;
  return info.status === 'ending' ? `${base} · ${info.daysLeft} day${info.daysLeft === 1 ? '' : 's'} left` : base;
};

const Certificates = () => {
  const reduceMotion = useReducedMotion();
  const [copiedId, setCopiedId] = useState(null);
  const copyTimer = useRef(null);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  // Active and lifetime first, then ending soon, then expired; original order within a group.
  const items = useMemo(() => {
    const now = new Date();
    return certifications
      .map((cert, index) => ({ cert, index, info: describe(cert, now) }))
      .sort((a, b) => STATUS[a.info.status].rank - STATUS[b.info.status].rank || a.index - b.index);
  }, []);

  const copyId = async (cert) => {
    const copied = await copyText(cert.validationNumber);

    if (copied) {
      setCopiedId(cert.id);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopiedId(null), 1600);
    }
  };

  // One entrance pattern for the whole page; disabled for reduced-motion users.
  const rise = (delay = 0) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.15 },
          transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }
        };

  return (
    <section className="certificates-section" id="certificates">
      <div className="certificates-container">
        <SectionHeader
          eyebrow="Credentials"
          lead="Professional"
          accent="Certifications"
          subtitle="Cloud, AI, and engineering credentials from AWS, Red Hat, Google, Microsoft, and HackerRank."
          {...rise()}
        />

        <div className="cert-grid" style={{ '--rows': Math.ceil(items.length / 2) }}>
          {items.map(({ cert, info }, index) => (
            <motion.article
              key={cert.id}
              className={`cert-card is-${info.status}`}
              style={{ '--c': cert.color }}
              {...rise((index % 2) * 0.07)}
            >
              <div className="cert-badge">
                <img src={cert.badgeImage} alt={`${cert.title} badge`} loading="lazy" />
              </div>

              <div className="cert-main">
                <div className="cert-top">
                  <span className="cert-issuer">{cert.issuer}</span>
                  <span className={`cert-status is-${info.status}`}>
                    {info.status === 'lifetime' ? (
                      <InfinityIcon size={12} aria-hidden="true" />
                    ) : (
                      <i aria-hidden="true" />
                    )}
                    {STATUS[info.status].label}
                  </span>
                </div>

                <h3 className="cert-title">{cert.title}</h3>

                <p className="cert-dates">
                  <span>Issued {formatDate(cert.issued)}</span>
                  <span className="cert-valid">{validityText(cert, info)}</span>
                </p>

                {info.elapsed !== null && (
                  <div
                    className="cert-meter"
                    role="img"
                    aria-label={`${Math.round(info.elapsed * 100)}% of the validity period has passed`}
                  >
                    <i style={{ width: `${Math.round(info.elapsed * 100)}%` }} />
                  </div>
                )}

                <div className="cert-foot">
                  <button
                    type="button"
                    className="cert-id"
                    onClick={() => copyId(cert)}
                    aria-label={`Copy credential ID ${cert.validationNumber}`}
                  >
                    <span className="cert-id-label">ID</span>
                    <code>{cert.validationNumber}</code>
                    {copiedId === cert.id ? <Check size={14} /> : <Copy size={14} />}
                  </button>

                  <div className="cert-links">
                    <a
                      href={cert.validationLink}
                      className="cert-link cert-link-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Verify ${cert.title}`}
                    >
                      <ShieldCheck size={15} />
                      Verify
                    </a>
                    {cert.credlyLink && (
                      <a
                        href={cert.credlyLink}
                        className="cert-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${cert.title} on Credly`}
                      >
                        Credly
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <span className="cert-live-region" aria-live="polite">
          {copiedId ? 'Credential ID copied' : ''}
        </span>
      </div>
    </section>
  );
};

export default Certificates;
